import {
  CHECKLIST_MAX_ITEMS,
  MARGIN_MM,
  SPACING_MM,
  TITLE_MAX_CHARS,
} from '../print-forms/model';
import type { PrintContentPack } from './types';

const S = SPACING_MM;
const M = MARGIN_MM;

export const printContentKo: PrintContentPack = {
  formNames: {
    lined: '줄 노트',
    grid: '모눈종이',
    weekly: '주간 계획표',
    checklist: '체크리스트',
  },
  formBlurbs: {
    lined: `줄 간격 ${S.lined.min}~${S.lined.max}mm의 줄 노트 PDF. 제목을 넣어 인쇄합니다.`,
    grid: `${S.grid.min}~${S.grid.max}mm 칸의 모눈종이 PDF. 5칸마다 굵은 선이 들어갑니다.`,
    weekly: '월요일 또는 일요일부터 시작하는 한 주 계획표와 메모 칸.',
    checklist: '네모 칸과 줄이 있는 체크리스트. 항목을 미리 적어 넣을 수 있습니다.',
  },

  homeHeading: '인쇄 양식 만들기',
  homeIntro:
    '줄 노트, 모눈종이, 주간 계획표, 체크리스트를 A4나 Letter 크기 PDF로 만들어 바로 인쇄합니다. 제목과 여백, 간격을 정하면 미리보기와 같은 파일이 내려받아집니다.',

  hub: {
    title: '인쇄 양식 만들기 — 줄 노트·모눈종이·주간 계획표·체크리스트 PDF',
    description:
      '줄 노트, 모눈종이, 주간 계획표, 체크리스트를 A4 또는 US Letter PDF로 만듭니다. 제목과 여백, 줄 간격을 정해 미리 보고 내려받아 실제 크기로 인쇄합니다.',
    h1: '인쇄 양식 만들기',
    intro: [
      '필요한 양식을 고르고 제목, 용지 크기, 여백, 간격을 정하면 한 페이지짜리 PDF가 만들어집니다. 미리보기는 내려받을 PDF를 그대로 그린 것입니다.',
      '입력한 제목과 항목은 이 브라우저 안에서만 쓰이고 어디에도 보내지 않습니다. 회원가입도 없습니다.',
    ],
    faq: [
      {
        q: '어떤 용지 크기를 고를 수 있나요?',
        a: 'A4(210×297mm)와 US Letter(8.5×11인치) 두 가지입니다. 프린터에 넣은 용지와 같은 크기를 고르세요.',
      },
      {
        q: '여러 장을 한 번에 만들 수 있나요?',
        a: 'PDF는 한 페이지입니다. 여러 장이 필요하면 인쇄 창에서 매수를 늘리면 됩니다.',
      },
    ],
  },

  forms: {
    lined: {
      title: '줄 노트 PDF 만들기 — 줄 간격을 정해 A4·Letter로 인쇄',
      description: `줄 간격(${S.lined.min}~${S.lined.max}mm)과 여백을 정해 줄 노트를 PDF로 만듭니다. 제목을 넣을 수 있고, A4와 US Letter를 지원하며 브라우저에서 바로 내려받습니다.`,
      h1: '줄 노트 만들기',
      intro: [
        `가로줄만 있는 연습장 한 장을 만듭니다. 줄 간격 ${S.lined.default}mm가 보통 공책과 비슷하고, 어린이 글씨 연습에는 10~12mm처럼 넓게 잡습니다.`,
      ],
      steps: [
        '용지 크기(A4 또는 Letter)를 고릅니다.',
        `필요하면 맨 위에 들어갈 제목을 적습니다(최대 ${TITLE_MAX_CHARS}자, 비워 두면 제목 없이 만듭니다).`,
        `여백(${M.min}~${M.max}mm)과 줄 간격(${S.lined.min}~${S.lined.max}mm)을 정하고 미리보기를 확인합니다.`,
        '‘PDF 내려받기’를 눌러 저장한 뒤 실제 크기(100%)로 인쇄합니다.',
      ],
      faq: [
        {
          q: '줄 간격은 얼마로 하면 좋나요?',
          a: '성인 필기는 7~8mm, 초등 저학년 글씨 연습은 10~12mm 정도가 흔히 쓰입니다. 바꿀 때마다 미리보기가 새로 그려지니 직접 비교해 보세요.',
        },
        {
          q: '인쇄하니 줄 간격이 조금 다릅니다.',
          a: '인쇄 창에서 ‘페이지에 맞춤’이 켜져 있으면 전체가 줄어듭니다. ‘실제 크기’ 또는 배율 100%로 바꿔 인쇄하세요.',
        },
      ],
    },

    grid: {
      title: '모눈종이 PDF 만들기 — 칸 크기를 mm로 정해 인쇄',
      description: `${S.grid.min}~${S.grid.max}mm 칸 크기로 모눈종이 PDF를 만듭니다. 5칸마다 굵은 선이 있어 세기 쉽고, A4·US Letter 중에서 고릅니다. 브라우저에서 바로 내려받습니다.`,
      h1: '모눈종이 만들기',
      intro: [
        `정사각형 칸이 채워진 모눈종이 한 장을 만듭니다. 칸 크기 ${S.grid.default}mm가 일반적인 모눈종이이고, 5칸마다 선이 진해져서 길이를 세기 쉽습니다.`,
      ],
      steps: [
        '용지 크기를 고르고 필요하면 제목을 적습니다.',
        `칸 크기(${S.grid.min}~${S.grid.max}mm)와 여백을 정합니다. 칸은 여백 안에 들어가는 만큼만 그리고, 좌우 여백이 같도록 가운데에 맞춥니다.`,
        '미리보기를 확인하고 ‘PDF 내려받기’를 누릅니다.',
        '실제 크기(100%)로 인쇄해야 칸 크기가 정확합니다.',
      ],
      faq: [
        {
          q: '칸 크기가 정확히 맞나요?',
          a: 'PDF 안에서는 지정한 mm 그대로 그립니다. 인쇄물은 배율 설정과 프린터에 따라 달라질 수 있으니 실제 크기로 인쇄한 뒤 자로 한 번 확인하세요.',
        },
        {
          q: '마지막 칸이 잘려 보입니다.',
          a: '잘린 칸은 그리지 않습니다. 여백 안에 온전히 들어가는 칸만 그리므로 남는 폭은 양쪽 여백에 나뉩니다.',
        },
      ],
    },

    weekly: {
      title: '주간 계획표 PDF 만들기 — 월요일·일요일 시작 선택',
      description:
        '월요일 또는 일요일부터 시작하는 한 주 계획표를 PDF로 만듭니다. 요일마다 날짜 적는 칸과 줄이 있고 메모 칸이 하나 더 있습니다. A4와 US Letter를 지원합니다.',
      h1: '주간 계획표 만들기',
      intro: [
        '일곱 요일과 메모 칸을 두 열로 나눈 한 장짜리 주간 계획표입니다. 요일 옆에 날짜를 적는 칸이 있어 날짜가 정해지지 않은 채로 여러 주에 다시 쓸 수 있습니다.',
      ],
      steps: [
        '용지 크기와 주 시작 요일(월요일 또는 일요일)을 고릅니다.',
        '필요하면 ‘6월 둘째 주’처럼 제목을 적습니다.',
        '여백을 정하고 미리보기에서 칸 배치를 확인합니다.',
        '‘PDF 내려받기’를 눌러 저장하고 실제 크기(100%)로 인쇄합니다.',
      ],
      faq: [
        {
          q: '요일 이름을 영어로 인쇄할 수 있나요?',
          a: '영어 페이지(English)에서 만들면 요일이 Monday, Tuesday처럼 영어로 들어갑니다.',
        },
        {
          q: '날짜가 미리 인쇄되나요?',
          a: '아니요. 날짜는 요일 옆 빈칸에 직접 적습니다. 같은 파일을 매주 다시 인쇄해 쓸 수 있습니다.',
        },
      ],
    },

    checklist: {
      title: '체크리스트 PDF 만들기 — 할 일 항목을 미리 넣어 인쇄',
      description: `네모 칸과 줄이 있는 체크리스트를 PDF로 만듭니다. 항목을 한 줄에 하나씩 최대 ${CHECKLIST_MAX_ITEMS}개까지 미리 적어 넣을 수 있고, 줄 높이와 여백을 정합니다.`,
      h1: '체크리스트 만들기',
      intro: [
        '왼쪽에 체크 칸, 오른쪽에 적는 줄이 있는 목록을 만듭니다. 장보기, 준비물, 청소 순서처럼 반복해서 쓰는 목록은 항목을 미리 넣어 두면 인쇄해서 바로 씁니다.',
      ],
      steps: [
        '용지 크기를 고르고 제목을 적습니다.',
        `항목 칸에 한 줄에 하나씩 적습니다. 비워 두면 빈 체크리스트가 됩니다(최대 ${CHECKLIST_MAX_ITEMS}개).`,
        `줄 높이(${S.checklist.min}~${S.checklist.max}mm)와 여백을 정합니다. 항목이 줄 수보다 많거나 한 줄에 들어가지 않으면 알려 줍니다.`,
        '‘PDF 내려받기’를 누르고 실제 크기(100%)로 인쇄합니다.',
      ],
      faq: [
        {
          q: '항목이 너무 길면 어떻게 되나요?',
          a: '잘라 내거나 글자를 줄이지 않고, 몇 번째 항목이 한 줄에 들어가지 않는지 알려 줍니다. 항목을 짧게 고치거나 여백을 줄이면 됩니다.',
        },
        {
          q: '한자나 이모지도 쓸 수 있나요?',
          a: '한글, 영문, 숫자와 흔한 기호는 됩니다. 한자와 이모지는 들어 있는 글꼴에 없어서, 어떤 글자가 안 되는지 표시하고 내려받기를 막습니다.',
        },
      ],
    },
  },

  printGuideHeading: '실제 크기(100%)로 인쇄하기',
  printGuide: [
    '내려받은 PDF를 열고 인쇄 창을 엽니다.',
    '크기 옵션에서 ‘실제 크기’ 또는 ‘배율 100%’를 고릅니다. ‘페이지에 맞춤’이나 ‘축소’는 끕니다.',
    '용지 크기를 PDF와 같은 A4 또는 Letter로 맞춥니다.',
    '처음 한 장은 자로 줄 간격이나 칸 크기를 확인해 보세요. 프린터마다 가장자리 인쇄 가능 범위가 다릅니다.',
  ],
  editorHeading: '양식 설정과 미리보기',
  noScript:
    '이 양식은 자바스크립트로 브라우저 안에서 PDF를 만듭니다. 아래 설명과 질문 답변은 그대로 읽을 수 있습니다.',
  otherFormsHeading: '다른 양식',
  backToHub: '인쇄 양식 전체 보기',
};
